import { useEffect, useRef, useState } from "react";
import { cn } from "./cn.js";
import { focusRing } from "./interactive.js";

/** A button that confirms when HELD down (holdMs, default 600ms) —
 *  so an accidental touch on a tablet does nothing (ALL OFF, score −,
 *  QIZIL). Progress is shown with a conic-gradient ring.
 *  Progress is a JS interval with a 50ms step — not a CSS transition, because on cancel
 *  it must return to zero immediately; the motion is minimal, no separate
 *  branch is required for reduced-motion. */
export function HoldButton({
  onConfirm,
  holdMs = 600,
  className,
  disabled,
  title,
  children,
}: {
  onConfirm: () => void;
  holdMs?: number;
  className?: string;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  const [progress, setProgress] = useState(0); // 0..1
  const timerRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  const stop = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(0);
  };
  useEffect(() => stop, []);

  // If it becomes disabled during the press the hold is cancelled — a safety button
  // (ALL OFF, QIZIL) must not fire in an invalid state.
  useEffect(() => {
    if (disabled) stop();
  }, [disabled]);

  const start = (e: React.PointerEvent) => {
    if (disabled) return;
    // A second finger must not steal a hold already in progress — otherwise
    // timerRef gets overwritten when the first finger lifts, and the orphaned interval
    // can fire onConfirm even AFTER the fingers are lifted (ALL OFF
    // by accident!). We start a new hold only if the interval is free.
    if (timerRef.current !== null) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    firedRef.current = false;
    startRef.current = performance.now();
    timerRef.current = window.setInterval(() => {
      const p = Math.min(1, (performance.now() - startRef.current) / holdMs);
      setProgress(p);
      if (p >= 1 && !firedRef.current) {
        firedRef.current = true;
        stop();
        onConfirm();
      }
    }, 50);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(focusRing, "relative select-none touch-none overflow-hidden", className)}
      style={
        progress > 0
          ? {
              backgroundImage: `conic-gradient(currentColor ${progress * 360}deg, transparent 0deg)`,
              backgroundBlendMode: "soft-light",
            }
          : undefined
      }
    >
      {children}
    </button>
  );
}
